// A quota's grants (REQ-082, API-231): the list, newest first, with expired and revoked ones; a
// row opens its summary, where an active grant can be revoked (confirmed). "Grant increase" adds
// an increase or an exemption for the whole scope or, on a per-member quota, one person, for at
// most 31 days, with a reason. Grants never edit the quota. Both writes are audited.
import { useEffect, useState } from "preact/hooks";
import { useApi } from "../../api/context";
import type { ApiError } from "../../api/errors";
import type { OrgContext } from "../../api/org-context";
import { useReadOnly } from "../../host/readonly";
import { ConfirmDialog, Dialog } from "../../primitives/dialog";
import { errorId, Field, InputAffix } from "../../primitives/field";
import { fieldErrorsFrom } from "../../primitives/forms";
import { Card, Chip } from "../../primitives/page";
import { RowDetail } from "../../primitives/row-detail";
import { Empty, ErrorBanner, ErrorDetails } from "../../primitives/states";
import { DataTable, type Column } from "../../primitives/table";
import {
  blankGrantForm,
  createGrant,
  grantExpiry,
  grantMembers,
  grantRows,
  isMemberScope,
  loadable,
  loadGrants,
  NOT_AVAILABLE,
  asApiError,
  revokeGrant,
  toGrantRequest,
  utcMoment,
  validateGrantForm,
  writeFailure,
  type GrantFormValues,
  type GrantRow,
  type Loadable,
  type Quota,
  type QuotaGrant,
  type User,
} from "./model";
import { personName } from "./names";

const COLUMNS: readonly Column<GrantRow>[] = [
  { key: "what", header: "Grant", help: "quotas.grantKind", cell: (row) => row.what },
  { key: "who", header: "For", cell: (row) => row.who },
  { key: "reason", header: "Reason", cell: (row) => <span class="quota-reason">{row.reason}</span> },
  { key: "expires", header: "Ends", cell: (row) => row.expires },
  { key: "status", header: "Status", cell: (row) => <Chip tone={row.statusTone}>{row.statusLabel}</Chip> },
];

export interface GrantsCardProps {
  context: OrgContext;
  quota: Quota;
  users: readonly User[];
  /** Whether the viewer may change this quota, and so grant on it. */
  canWrite: boolean;
  now?: Date;
}

export function GrantsCard({ context, quota, users, canWrite, now = new Date() }: GrantsCardProps) {
  const client = useApi();
  const [state, setState] = useState<Loadable<QuotaGrant[]>>({ kind: "loading" });
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<GrantRow | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiError | null>(null);

  const load = () => {
    setState({ kind: "loading" });
    void loadable(() => loadGrants(client, context.orgId, quota.id)).then(setState);
  };
  useEffect(load, [client, context.orgId, quota.id]);

  const revoke = () => {
    if (!open) return;
    setBusy(true);
    setFailure(null);
    revokeGrant(client, open.grant)
      .then(() => {
        setRevoking(false);
        setOpen(null);
        load();
      })
      .catch((error: unknown) => setFailure(asApiError(error)))
      .finally(() => setBusy(false));
  };

  const rows = state.kind === "loaded" ? grantRows(state.value, quota, context.moneyPolicy, now) : [];
  return (
    <Card
      title="Grants"
      help="quotas.grantKind"
      actions={
        canWrite &&
        state.kind !== "unavailable" && (
          <button type="button" class="button button-primary button-sm" onClick={() => setCreating(true)}>
            Grant increase
          </button>
        )
      }
    >
      {state.kind === "unavailable" ? (
        <Empty message={NOT_AVAILABLE} />
      ) : (
        <DataTable
          caption="Grants"
          hideCaption
          columns={COLUMNS}
          rows={rows}
          rowKey={(row) => row.id}
          loading={state.kind === "loading"}
          error={state.kind === "error" ? state.error : null}
          onRetry={load}
          emptyMessage="No grants on this quota."
          onRowClick={setOpen}
          rowLabel={(row) => `Open the grant ${row.what} for ${row.who}`}
        />
      )}
      {open && (
        <RowDetail
          title={`Grant ${open.what}`}
          open
          onClose={() => {
            setOpen(null);
            setFailure(null);
          }}
          facts={[
            { label: "For", value: open.who },
            { label: "Reason", value: open.reason },
            { label: "Starts", value: utcMoment(open.grant.startsAt) },
            { label: "Ends", value: open.expires },
            { label: "Given by", value: open.grant.createdBy },
            { label: "Status", value: <Chip tone={open.statusTone}>{open.statusLabel}</Chip> },
          ]}
          actions={
            canWrite &&
            open.revocable && (
              <button type="button" class="button button-danger" onClick={() => setRevoking(true)}>
                Revoke
              </button>
            )
          }
        >
          {failure && !revoking && <p class="banner banner-error" role="alert">{writeFailure(failure)}</p>}
        </RowDetail>
      )}
      <ConfirmDialog
        title="Revoke this grant?"
        open={revoking}
        message={
          <>
            <p>The grant ends now; the quota's own limit applies again on every proxy within the sync interval.</p>
            {failure && <p role="alert">{writeFailure(failure)}</p>}
          </>
        }
        confirmLabel="Revoke"
        busy={busy}
        onConfirm={revoke}
        onClose={() => setRevoking(false)}
      />
      {creating && (
        <GrantDialog
          context={context}
          quota={quota}
          users={users}
          now={now}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            load();
          }}
        />
      )}
    </Card>
  );
}

export interface GrantDialogProps {
  context: OrgContext;
  quota: Quota;
  users: readonly User[];
  now: Date;
  onClose(): void;
  onCreated(grant: QuotaGrant): void;
}

export function GrantDialog({ context, quota, users, now, onClose, onCreated }: GrantDialogProps) {
  const client = useApi();
  const { readOnly, reason } = useReadOnly();
  const [values, setValues] = useState<GrantFormValues>(blankGrantForm);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<ApiError | null>(null);

  const clientErrors = validateGrantForm(values, quota);
  const errors = { ...clientErrors, ...(failure ? fieldErrorsFrom(failure) : {}) };
  const shown = (field: string) => (touched ? errors[field] : undefined);
  const set = <K extends keyof GrantFormValues>(key: K, value: GrantFormValues[K]) => {
    setFailure(null);
    setValues((current) => ({ ...current, [key]: value }));
  };

  const save = () => {
    setTouched(true);
    if (Object.keys(clientErrors).length > 0 || saving) return;
    setSaving(true);
    setFailure(null);
    createGrant(client, context.orgId, quota.id, toGrantRequest(values, quota, new Date()))
      .then(onCreated)
      .catch((error: unknown) => setFailure(asApiError(error)))
      .finally(() => setSaving(false));
  };

  return (
    <Dialog
      title={`Grant on ${quota.name}`}
      open
      onClose={onClose}
      closeLabel="Close"
      actions={
        <>
          <button type="button" class="button button-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" class="button button-primary" disabled={readOnly || saving} title={readOnly ? reason ?? "" : undefined} aria-describedby={readOnly ? "quotas-stale-note" : undefined} onClick={save}>
            {saving ? "Granting…" : "Grant"}
          </button>
        </>
      }
    >
      <form
        class="stack"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        {failure && (
          <div class="banner banner-error" role="alert">
            <p>{writeFailure(failure)}</p>
            <ErrorDetails error={failure} />
          </div>
        )}
        <Field id="grant-kind" label="Grant" help="quotas.grantKind">
          <select id="grant-kind" value={values.kind} onChange={(event) => set("kind", event.currentTarget.value as GrantFormValues["kind"])}>
            <option value="increase">Increase the limit</option>
            <option value="exempt">Exempt from the limit</option>
          </select>
        </Field>
        {values.kind === "increase" && (
          <Field id="grant-amount" label="Increase by" help="quotas.grantAmount" error={shown("amount")}>
            <InputAffix {...(quota.metric === "spend" ? { prefix: "$" } : { suffix: quota.metric })}>
              <input id="grant-amount" type="text" inputMode="decimal" value={values.amount} onInput={(event) => set("amount", event.currentTarget.value)} />
            </InputAffix>
          </Field>
        )}
        {isMemberScope(quota.scope) && (
          <Field id="grant-member" label="For" help="quotas.grantMember" error={shown("userId")}>
            <select id="grant-member" value={values.userId} onChange={(event) => set("userId", event.currentTarget.value)}>
              <option value="">Every member</option>
              {grantMembers(quota, users).map((user) => (
                <option key={user.id} value={user.id}>
                  {personName(user)}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field id="grant-days" label="Days" help="quotas.grantDays" hint={`Ends ${utcMoment(grantExpiry(now, values.days))}.`} error={shown("days")}>
          <input id="grant-days" type="number" min={1} max={31} value={values.days} onInput={(event) => set("days", event.currentTarget.value)} />
        </Field>
        <Field id="grant-reason" label="Reason" help="quotas.grantReason" error={shown("reason")}>
          <textarea
            id="grant-reason"
            rows={3}
            maxLength={500}
            value={values.reason}
            aria-describedby={shown("reason") ? errorId("grant-reason") : undefined}
            onInput={(event) => set("reason", event.currentTarget.value)}
          />
        </Field>
      </form>
    </Dialog>
  );
}
